import { ArrowLeft, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { RecipeView } from '../../shared/api';
import { MEAL_LABELS, MEALS, mealForHour, type Meal } from '../../shared/dates';
import { scaleMap } from '../../shared/nutrients';
import { safeHttpUrl } from '../../shared/recipes';
import { ApiError } from '../api/client';
import { useDeleteRecipe, useRecipe, useSettings } from '../api/hooks';
import { FatSecretAttribution } from '../components/FatSecretAttribution';
import { GramsInput } from '../components/GramsInput';
import { useLogItems } from '../components/log/useLogItems';
import { NutritionPanel } from '../components/NutritionPanel';
import { draftFrom, type RecipeDraft } from '../components/recipes/draft';
import { LazyRecipeEditor } from '../components/recipes/LazyRecipeEditor';
import { grams as formatGrams, kcal, pluralize } from '../lib/format';
import { localHour } from '../lib/today';
import { useUI } from '../lib/ui';

/** Log some of the recipe to today: a serving by default, any amount in grams, in the meal chosen. */
function LogRecipe({ recipe }: { recipe: RecipeView }) {
  const { today } = useUI();
  const titleId = useId();
  const food = recipe.food;
  const [grams, setGrams] = useState(food.servings[0]?.grams ?? 100);
  const [meal, setMeal] = useState<Meal>(() => mealForHour(localHour()));
  const { log, pending } = useLogItems(today, meal);
  const n = scaleMap(food.per100, grams);
  return (
    <section className="card card--pad stack recipe-log" style={{ gap: 14 }} aria-labelledby={titleId}>
      <h2 id={titleId} className="card__title">
        Log it
      </h2>
      <GramsInput value={grams} onChange={setGrams} servings={food.servings} kcalPer100={food.per100.kcal} label="Amount" />
      <div className="seg seg--block" role="radiogroup" aria-label="Meal">
        {MEALS.map((m) => (
          <button key={m} role="radio" aria-checked={meal === m} className={`seg__btn ${meal === m ? 'is-active' : ''}`} onClick={() => setMeal(m)}>
            {MEAL_LABELS[m]}
          </button>
        ))}
      </div>
      {/* aria-disabled while logging keeps keyboard focus here (useLogItems ignores a second press). */}
      <button
        className="btn btn--primary btn--lg btn--block"
        aria-disabled={pending || undefined}
        onClick={() => {
          if (pending) return;
          void log([{ name: recipe.name, brand: null, foodRef: food.ref, grams, per100: food.per100, servings: food.servings, source: 'recipe', confidence: null }]);
        }}
      >
        Log to {MEAL_LABELS[meal].toLowerCase()} · {kcal(n.kcal)} kcal
      </button>
      <p className="tiny muted" style={{ marginTop: -6 }}>
        Adds it to today's log.
      </p>
    </section>
  );
}

/** A recipe: logging it, its nutrition per serving, ingredients, notes and the link it came from. */
export function RecipePage() {
  const { id = '' } = useParams();
  const { toast } = useUI();
  const navigate = useNavigate();
  const location = useLocation();
  const recipe = useRecipe(id);
  const settings = useSettings();
  const remove = useDeleteRecipe();
  const [editing, setEditing] = useState<RecipeDraft | null>(null);
  const [confirming, setConfirming] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const deleteRef = useRef<HTMLButtonElement>(null);
  const promptId = useId();
  const nutritionId = useId();
  const ingredientsId = useId();
  const notesId = useId();
  const defs = useMemo(() => new Map((settings.data?.nutrients ?? []).map((n) => [n.key, n])), [settings.data]);
  const tracked = settings.data?.tracking.filter((t) => t.tracked).map((t) => t.key) ?? ['kcal', 'protein', 'carbs', 'fat'];
  const data = recipe.data;
  const notFound = recipe.error instanceof ApiError && recipe.error.status === 404;

  // Just made (or arrived from the list after a change): focus lands on its name.
  const loaded = Boolean(data);
  useEffect(() => {
    if (loaded && (location.state as { focusHeading?: boolean } | null)?.focusHeading) headingRef.current?.focus();
  }, [loaded, location.state]);

  if (notFound || (recipe.isError && !data)) {
    return (
      <div className="page">
        <Link to="/recipes" className="btn btn--quiet btn--sm" style={{ alignSelf: 'flex-start' }}>
          <ArrowLeft size={16} aria-hidden="true" /> Recipes
        </Link>
        <section className="card card--pad stack" role="alert">
          <h1 className="section-title">{notFound ? 'Recipe not found' : "Couldn't load this recipe"}</h1>
          <p className="small muted">{notFound ? 'It may have been deleted. What you logged from it is still in your log.' : recipe.error?.message}</p>
        </section>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page" aria-busy="true">
        <div className="skeleton" style={{ height: 34, width: 120 }} />
        <div className="skeleton" style={{ height: 48 }} />
        <div className="skeleton" style={{ height: 220, borderRadius: 20 }} />
      </div>
    );
  }

  const link = data.sourceUrl ? safeHttpUrl(data.sourceUrl) : null;
  const cancelDelete = () => {
    setConfirming(false);
    requestAnimationFrame(() => deleteRef.current?.focus());
  };
  const del = () => {
    if (remove.isPending) return;
    remove.mutate(data.id, {
      onSuccess: () => {
        toast(`Deleted ${data.name}`);
        // Replacing this page in history, so Back doesn't open the recipe that's gone.
        navigate('/recipes', { replace: true, state: { focusHeading: true } });
      },
      onError: (e) => toast(e.message, { tone: 'error' }),
    });
  };

  return (
    <div className="page">
      <Link to="/recipes" className="btn btn--quiet btn--sm" style={{ alignSelf: 'flex-start', marginBottom: -8 }}>
        <ArrowLeft size={16} aria-hidden="true" /> Recipes
      </Link>
      <div className="page__head">
        <div className="grow">
          <h1 ref={headingRef} tabIndex={-1} className="page__title recipe-title">
            {data.name}
          </h1>
          <p className="page__sub num">
            {pluralize(data.servings, 'serving')} · {formatGrams(data.servingGrams)} each
          </p>
        </div>
      </div>
      <div className="row wrap">
        {/* Keyed, so the prompt's buttons are new elements and Cancel takes focus (never a reused Delete). */}
        {confirming ? (
          <div key="confirm" className="row wrap" role="group" aria-label={`Delete ${data.name}?`}>
            <span id={promptId} className="small" style={{ flexBasis: '100%' }}>
              Delete this recipe? What you've logged from it stays in your log.
            </span>
            <button className="btn btn--danger btn--sm" aria-disabled={remove.isPending || undefined} aria-describedby={promptId} onClick={del}>
              Yes, delete
            </button>
            {/* Focus lands on the safe choice; deleting is a deliberate second step. */}
            <button className="btn btn--ghost btn--sm" autoFocus aria-describedby={promptId} onClick={cancelDelete}>
              Cancel
            </button>
          </div>
        ) : (
          <div key="actions" className="row wrap">
            <button className="btn btn--ghost btn--sm" onClick={() => setEditing(draftFrom(data))}>
              <Pencil size={15} aria-hidden="true" /> Edit
            </button>
            <button ref={deleteRef} className="btn btn--quiet btn--sm" onClick={() => setConfirming(true)}>
              <Trash2 size={15} aria-hidden="true" /> Delete
            </button>
          </div>
        )}
      </div>

      {/* Another recipe, or a new version of this one, starts again from one serving. */}
      <LogRecipe key={`${data.id}:${data.version}`} recipe={data} />

      <section className="card card--pad" aria-labelledby={nutritionId}>
        <h2 id={nutritionId} className="card__title">
          Nutrition per serving
        </h2>
        <p className="small muted" style={{ margin: '4px 0 6px' }}>
          One serving is {formatGrams(data.servingGrams)}
          {data.cookedGrams ? ' of the cooked dish' : ''}.
        </p>
        <NutritionPanel
          per100={data.food.per100}
          grams={data.servingGrams}
          defs={defs}
          tracked={tracked}
          coverage={{ known: data.known, count: data.ingredientCount }}
        />
      </section>

      <section className="card card--pad" aria-labelledby={ingredientsId}>
        <h2 id={ingredientsId} className="card__title">
          Ingredients
        </h2>
        <ul className="recipe-ingredients">
          {data.ingredients.map((i) => (
            <li key={i.id}>
              <span className="grow recipe-ingredients__what">
                <span className="recipe-ingredients__name">{i.name}</span>
                {i.brand && <span className="tiny muted"> · {i.brand}</span>}
              </span>
              <span className="num small recipe-ingredients__grams">{formatGrams(i.grams, 1)}</span>
              <span className="num small muted recipe-ingredients__kcal">{kcal(scaleMap(i.per100, i.grams).kcal)} kcal</span>
            </li>
          ))}
        </ul>
        <p className="tiny muted" style={{ marginTop: 8 }}>
          {data.cookedGrams
            ? `${formatGrams(data.rawGrams)} raw, ${formatGrams(data.cookedGrams)} cooked.`
            : `${formatGrams(data.rawGrams)} raw. No cooked weight, so grams count as raw ingredients.`}
        </p>
        {data.ingredients.some((i) => i.foodRef?.startsWith('fs:')) && <FatSecretAttribution />}
      </section>

      {data.notes && (
        <section className="card card--pad" aria-labelledby={notesId}>
          <h2 id={notesId} className="card__title">
            Notes
          </h2>
          <p className="recipe-notes small">{data.notes}</p>
        </section>
      )}

      {link && (
        <a className="btn btn--ghost btn--sm btn--wrap" style={{ alignSelf: 'flex-start' }} href={link} target="_blank" rel="noopener noreferrer nofollow">
          <ExternalLink size={15} aria-hidden="true" /> Original recipe
          <span className="muted">· {new URL(link).hostname}</span>
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      )}

      {editing && (
        <LazyRecipeEditor
          recipeId={data.id}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}
